import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useAttachmentStaging } from '@/hooks/useAttachmentStaging';
import { useEntityComments } from '@/hooks/useEntityComments';
import type { GenericAttachment } from '@/styles/GenericAttachmentsSection';
import { GenericForm, GenericFormField } from '@/styles/GenericForm';
import { GenericAttachmentsSection } from '@/styles/GenericAttachmentsSection';
import { GenericCommentsSection, TEAM_CLIENT_VENDOR_AUDIENCES } from '@/styles/GenericCommentsSection';
import { StandardButton, SaveChangesButton, CancelButton } from '@/styles/StandardButtons';
import { ChangeOrder, NewChangeOrderInput, ChangeOrderStatus } from '../types';
import { STATUS_OPTIONS, MAX_FILE_BYTES } from '../constants';
import { uploadChangeOrderAttachments, fetchChangeOrderAttachments, deleteChangeOrderAttachment } from '../store';
import { getApiUrl } from '@/lib/api-config';
import { useUser } from '@/contexts/UserContext';
import { useProject, getDefaultProjectIdForNewRecord } from '@/components/ProjectSelector';
import { CatalogLineItems, useCatalogLines } from '@/features/cost-management/purchase-orders/components/CatalogLineItems';
import { PurchaseOrderCreateDialog } from '@/features/cost-management/purchase-orders/components/PurchaseOrderCreateDialog';
import { usePurchaseOrderFormOptions } from '@/features/cost-management/purchase-orders/usePurchaseOrderFormOptions';
import { commonVendorOf, lineFromRow, linesProblem, toLinePayload } from '@/features/cost-management/purchase-orders/lib/po-lines';
import { fetchEntityList } from '@/lib/entity-api';
import { useCompanyLookups } from '@/hooks/useCompanyLookups';
import { useProjectMembershipPrompt } from '@/hooks/useProjectMembershipPrompt';
import { formatMoney, lineCostCodeProblem, linesTotal, newLineKey, type LineItem } from '@/lib/line-items';
import { statusLabel } from '@/lib/status-label';
import { derivedDescription } from '@/lib/derived-description';
import { LinkedDocsSection, type LinkedDoc } from '@/components/shared/LinkedDocsSection';

interface ChangeOrderFormProps {
  mode: 'create' | 'edit';
  initialData?: ChangeOrder | null;
  projects: { id: string; name: string }[];
  onSubmit: (data: NewChangeOrderInput) => Promise<ChangeOrder | undefined>;
  /** A change order created from this form (Create PO saves it first) is updated, not created again, by the next Save */
  onUpdateCreated?: (id: string, data: NewChangeOrderInput) => Promise<ChangeOrder | undefined>;
  onCancel: () => void;
  isOpen: boolean;
  /** Called whenever something the list page displays changed outside the
   *  form's own Save (attachment/comment added or removed, status moved
   *  server-side after Send to Client) so the page can re-sync counts/status. */
  onRelatedDataChanged?: () => void;
}

export function ChangeOrderForm({
  mode,
  initialData,
  projects,
  onSubmit,
  onUpdateCreated,
  onCancel,
  isOpen,
  onRelatedDataChanged,
}: ChangeOrderFormProps) {
  const { companyId, user } = useUser();
  const { users: people } = useCompanyLookups(isOpen && !!companyId);
  const { ensureMembers, prompt: membershipPrompt } = useProjectMembershipPrompt();
  const { selectedProject } = useProject();
  // a new change order that Create PO has already saved: from then on it is an existing one (it has an id) although the form is still the "new" form
  const [createdId, setCreatedId] = useState<string | null>(null);
  const changeOrderId = (mode === 'edit' ? initialData?.id : undefined) ?? createdId ?? undefined;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentFormData, setCurrentFormData] = useState<Record<string, any>>({});
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const initialFormDataRef = useRef<Record<string, any>>({});

  // -- Items: the same item table as a purchase order. Builder Price / Client Price are their total (never typed) ------------------------
  // `linesLoaded` is false while an existing change order's items are being read (or could not be): then they are never sent on save, so a
  // failed read can never wipe them.
  const [lines, setLines] = useState<LineItem[]>([]);
  const [linesLoaded, setLinesLoaded] = useState(true);
  const [linesLoading, setLinesLoading] = useState(false);
  const [linesLoadError, setLinesLoadError] = useState<string | null>(null);
  const initialLinesRef = useRef('[]');
  const serializeLines = (list: LineItem[]) => JSON.stringify(list.map(({ key: _key, ...rest }) => rest));
  const { vendors } = usePurchaseOrderFormOptions();
  const catalog = useCatalogLines({ isOpen, lines, setLines, vendorId: null, vendors });
  const { setOffersByItem, rememberOffersOf } = catalog;
  const linesChanged = () => linesLoaded && (changeOrderId ? serializeLines(lines) !== initialLinesRef.current : lines.length > 0);
  // a change order that has (or had) items shows their total; one from before items existed keeps the figures it has
  const usesItemTotal = linesLoaded && (lines.length > 0 || initialLinesRef.current !== '[]');
  const itemsTotal = linesTotal(lines);
  const shownPrice = usesItemTotal ? itemsTotal : Number(initialData?.builderCost ?? 0);

  // -- Linked Docs: the purchase order made from this change order (the only real link a change order has) -----------------------------------
  const [linkedDocs, setLinkedDocs] = useState<LinkedDoc[]>([]);
  const loadLinkedDocs = useCallback(async () => {
    if (!changeOrderId) { setLinkedDocs([]); return; }
    try {
      const orders = await fetchEntityList<any>('purchase_order', { filters: { change_order_id: changeOrderId } });
      setLinkedDocs(orders.map((po) => ({
        id: po.id,
        type: 'Purchase Order',
        entityType: 'purchase_order',
        label: po.title ? `${po.po_number} — ${po.title}` : po.po_number,
        detail: [po.status ? statusLabel(po.status) : '', po.amount != null ? formatMoney(Number(po.amount)) : ''].filter(Boolean).join(' · '),
      })));
    } catch (failure) {
      console.error('Error loading the linked documents:', failure);
    }
  }, [changeOrderId]);
  const [purchaseOrderStart, setPurchaseOrderStart] = useState<{ title: string; projectId: string; description: string; vendorId?: string; lines: LineItem[] } | null>(null);
  // the id of a change order created by a Save whose items then failed (or by Create PO): the next Save only retries what is missing
  const savedIdRef = useRef<string | null>(null);

  // -- Attachments: the shared staging hook + shared upload/fetch/delete ------
  const uploadFn = useCallback(async (id: string, files: File[]): Promise<GenericAttachment[]> => {
    if (!companyId || !user?.id) return [];
    const uploaded = await uploadChangeOrderAttachments(id, files, companyId, user.id);
    if (uploaded.length > 0) onRelatedDataChanged?.();
    return uploaded;
  }, [companyId, user, onRelatedDataChanged]);
  const deleteFn = useCallback(async (attachmentId: string): Promise<boolean> => {
    const ok = await deleteChangeOrderAttachment(attachmentId, user?.id);
    if (ok) onRelatedDataChanged?.();
    return ok;
  }, [user, onRelatedDataChanged]);
  const {
    pendingFiles: selectedFiles,
    uploadedFiles,
    setUploadedFiles,
    handleUpload: handleFileUploadBase,
    handleRemovePending: handleRemovePendingFile,
    handleDelete: handleDeleteFile,
    resetPending: resetSelectedFiles,
    uploadPending,
  } = useAttachmentStaging<GenericAttachment>({ entityId: changeOrderId, uploadFn, deleteFn });

  const handleFileUpload = useCallback(async (files: File[]) => {
    if (files.some(f => f.size > MAX_FILE_BYTES)) {
      setError("One or more files exceed 20MB limit.");
      return;
    }
    setError(null);
    await handleFileUploadBase(files);
  }, [handleFileUploadBase]);

  // Load the record's existing attachments when the edit form opens; clear
  // everything when it closes.
  useEffect(() => {
    if (isOpen && changeOrderId) {
      let cancelled = false;
      fetchChangeOrderAttachments(changeOrderId).then((rows) => { if (!cancelled) setUploadedFiles(rows); });
      return () => { cancelled = true; };
    }
    setUploadedFiles([]);
    resetSelectedFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, changeOrderId]);

  // The items of an existing change order are read once when it opens; a new one starts with none
  const storedId = mode === 'edit' ? initialData?.id : undefined;
  useEffect(() => {
    savedIdRef.current = null;
    setCreatedId(null);
    setLines([]);
    setOffersByItem(new Map());
    setLinesLoadError(null);
    initialLinesRef.current = '[]';
    setLinkedDocs([]);
    setPurchaseOrderStart(null);
    if (!isOpen || !storedId) { setLinesLoaded(true); setLinesLoading(false); return undefined; }
    let cancelled = false;
    setLinesLoaded(false);
    setLinesLoading(true);
    fetchEntityList<any>('change_order_item', { filters: { change_order_id: storedId } })
      .then((rows) => {
        if (cancelled) return;
        const loaded = [...rows].sort((a, b) => (a.order_number ?? 0) - (b.order_number ?? 0)).map((row) => lineFromRow(row, null));
        setLines(loaded);
        initialLinesRef.current = serializeLines(loaded);
        setLinesLoaded(true);
        rememberOffersOf(loaded.map((l) => l.catalogItemId).filter((id): id is string => !!id));
      })
      .catch((failure) => {
        if (cancelled) return;
        console.error('Error loading the change order items:', failure);
        setLinesLoadError('The items of this change order could not be loaded. They stay as they are: saving does not change them.');
      })
      .finally(() => { if (!cancelled) setLinesLoading(false); });
    void loadLinkedDocs();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, storedId]);

  // -- Comments: shared hook + shared section (Team / Client / Vendor) ---------
  const { comments, addComment, deleteComment } = useEntityComments('change_order', isOpen ? changeOrderId : null);
  const handleAddComment = useCallback(async (content: string, audience?: string) => {
    await addComment(content, audience);
    onRelatedDataChanged?.();
  }, [addComment, onRelatedDataChanged]);
  const handleDeleteComment = useCallback(async (commentId: string) => {
    await deleteComment(commentId);
    onRelatedDataChanged?.();
  }, [deleteComment, onRelatedDataChanged]);

  // Initial values for GenericForm
  const initialValues = useMemo(() => {
    if (mode === 'edit' && initialData) {
      return {
        projectId: initialData.projectId || '',
        assigneeId: initialData.assigneeId || '',
        title: initialData.title || '',
        description: initialData.description || '',
        clientCost: initialData.clientCost || 0,
        builderCost: initialData.builderCost || 0,
        approvalDeadline: initialData.approvalDeadline ? initialData.approvalDeadline.slice(0, 10) : '',
        status: initialData.status || 'draft',
      };
    }
    return {
      projectId: getDefaultProjectIdForNewRecord(selectedProject),
      assigneeId: '',
      title: '',
      description: '',
      clientCost: 0,
      builderCost: 0,
      approvalDeadline: '',
      status: 'draft',
    };
  }, [mode, initialData, selectedProject]);

  // Track initial form data for unsaved changes detection
  useEffect(() => {
    if (isOpen) {
      const deepCopy = JSON.parse(JSON.stringify(initialValues));
      initialFormDataRef.current = deepCopy;
      setCurrentFormData(deepCopy);
    }
  }, [isOpen, initialValues]);

  const hasFormChanged = (current: Record<string, any>, initial: Record<string, any>): boolean => {
    const allKeys = Array.from(new Set([...Object.keys(current), ...Object.keys(initial)]));
    for (const key of allKeys) {
      const currentStr = current[key] == null ? '' : String(current[key]).trim();
      const initialStr = initial[key] == null ? '' : String(initial[key]).trim();
      if (currentStr !== initialStr) return true;
    }
    return selectedFiles.length > 0 || linesChanged();
  };

  const handleCloseAttempt = () => {
    if (hasFormChanged(currentFormData, initialFormDataRef.current)) {
      if (!window.confirm("Are you sure you want to lose all unsaved progress?")) return;
    }
    onCancel();
  };

  const handleFieldChange = (name: string, value: any, updatedFormData: Record<string, any>) => {
    setCurrentFormData(updatedFormData);
  };

  const changeOrderFields = useMemo<GenericFormField[]>(() => [
    {
      name: 'title',
      label: 'Title',
      type: 'text',
      required: true,
      row: 1,
      placeholder: 'Enter title',
    },
    {
      name: 'projectId',
      label: 'Project',
      type: 'select',
      required: true,
      row: 1,
      options: projects.map(p => ({ label: p.name, value: p.id })),
      placeholder: 'Select',
    },
    {
      name: 'assigneeId',
      label: 'Assignee',
      type: 'select',
      row: 3,
      colSpan: 1,
      // anybody of the company, A-Z; the person must belong to the project (the shared popup asks)
      options: people.map(u => ({ label: u.company_name || u.full_name || u.email, value: u.id })),
      placeholder: 'Select',
      membershipProjectField: 'projectId',
    },
    {
      name: 'approvalDeadline',
      label: 'Approval Deadline',
      type: 'date',
      row: 2,
      colSpan: 0.5, // 1/4 width
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      required: true,
      hideRequiredMark: true, // always preselected (never blank), so no asterisk
      row: 2,
      colSpan: 0.5,
      options: STATUS_OPTIONS.map(s => ({ label: s.label, value: s.value })),
      placeholder: 'Select',
    },
    {
      name: 'builderCost',
      label: 'Builder Price ($)',
      type: 'text',
      row: 2,
      colSpan: 0.5,
      displayValue: formatMoney(shownPrice), // the total of the items: shown, never typed
    },
    {
      name: 'clientCost',
      label: 'Client Price ($)',
      type: 'number',
      row: 2,
      colSpan: 0.5, // the user's own price to the client: typed, saved as it is, never overwritten by the items
      placeholder: '$0.00',
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      row: 4,
      colSpan: 2,
      placeholder: 'Enter description',
    },
  ], [projects, shownPrice, people]);

  const handleExportPDF = () => {
    if (!changeOrderId) {
      setError('Cannot export PDF: Missing change order information');
      return;
    }
    // Send/Export separation (2026-09-14): Export PDF doesn't create a
    // snapshot/share token -- it opens the authenticated preview page, which
    // triggers window.print() itself (?print=1).
    // TODO(PDF): placeholder until the general KrewUp PDF format exists.
    window.open(`/documents/change_order/${changeOrderId}/preview?print=1`, '_blank', 'noopener,noreferrer');
  };

  const handleSendToClient = async () => {
    if (!changeOrderId || !companyId) {
      setError('Cannot send: missing change order or company information');
      return;
    }
    // The client email is built from the SAVED change order (server-side
    // snapshot), so unsaved edits would silently not be what the client sees.
    if (hasFormChanged(currentFormData, initialFormDataRef.current)) {
      setError('Save your changes before sending the change order to the client.');
      return;
    }

    setIsSendingEmail(true);
    setError(null);

    try {
      // The assignee reads the change order as a member of the project: ask to add them first (the server refuses otherwise)
      const assigneeId = currentFormData.assigneeId as string | undefined;
      if (assigneeId) {
        const person = people.find((u) => u.id === assigneeId);
        const allowed = await ensureMembers(currentFormData.projectId, [{ id: assigneeId, name: person?.company_name || person?.full_name || 'The assignee' }]);
        if (!allowed) { setError('The assignee must belong to the project before the change order can be sent.'); return; }
      }
      // Recipient (the project's client) and the Draft/Rejected -> Pending
      // Approval status change are both resolved server-side by the
      // change_order document definition.
      const response = await fetch(getApiUrl(`api/documents/change_order/${changeOrderId}/share`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        let message = 'Failed to send the change order';
        try {
          const errorData = await response.json();
          message = errorData.error || errorData.message || message;
        } catch (e) {
          message = response.statusText || message;
        }
        throw new Error(message);
      }

      const result = await response.json();
      if (!result.success) throw new Error(result.error || 'Failed to send the change order');
      if (!result.emailed) throw new Error('No client email found for this change order');

      alert('Change order sent to the client');
      // Status moved server-side (Pending Approval): re-sync the list and close
      // so reopening shows the persisted state.
      onRelatedDataChanged?.();
      onCancel();
    } catch (err) {
      console.error('Error sending change order:', err);
      setError(err instanceof Error ? err.message : 'Failed to send the change order to the client');
    } finally {
      setIsSendingEmail(false);
    }
  };

  /**
   * Saves the change order, then its items (they need its id and set its Builder / Client Price on the server). `close: false` is for Create PO, which
   * saves and keeps the form open. Returns whether everything was saved.
   */
  const handleSubmit = async (data: Record<string, any>, { close = true }: { close?: boolean } = {}): Promise<boolean> => {
    if (isSubmitting) return false;
    setError(null);

    // The items: every one must be saveable (checked here, never while the user is still preparing a line)
    if (linesChanged()) {
      const problem = linesProblem(lines) || lineCostCodeProblem(lines);
      if (problem) { setError(problem); return false; }
    }
    setIsSubmitting(true);

    try {
      // Title / Project are `required` fields -- GenericForm blocks the
      // submit itself (generic required validation).
      // Builder Price is the items' total (a change order from before items existed, and untouched by them, keeps its figure); Client Price is the user's own.
      const payload: NewChangeOrderInput = {
        title: (data.title || '').trim(),
        description: (data.description || '').trim(),
        projectId: data.projectId,
        assigneeId: data.assigneeId || null,
        clientCost: Number(data.clientCost || 0),
        builderCost: shownPrice,
        approvalDeadline: data.approvalDeadline || undefined,
        status: data.status as ChangeOrderStatus,
      };

      // a change order that was created by a previous attempt of this same Save is not created twice: only the missing part is retried
      let savedId = changeOrderId ?? savedIdRef.current;
      let result: ChangeOrder | undefined;
      if (storedId) {
        result = await onSubmit(payload);
      } else if (!savedId) {
        result = await onSubmit(payload);
        savedId = result?.id ?? null;
        savedIdRef.current = savedId;
        if (savedId) setCreatedId(savedId);
      } else if (createdId && onUpdateCreated) {
        await onUpdateCreated(createdId, payload); // created by Create PO earlier: this Save updates it
      }
      if (selectedFiles.length > 0 && savedId) {
        await uploadPending(savedId);
      }
      if (savedId && linesChanged()) {
        const response = await fetch(getApiUrl(`api/change-orders/${savedId}/lines`), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ lines: toLinePayload(lines) }),
        });
        if (!response.ok) {
          let message = 'unknown error';
          try { const body = await response.json(); message = body.error || body.message || message; } catch { /* keep the generic message */ }
          throw new Error(`The change order was saved, but its items could not be saved: ${message}`);
        }
        initialLinesRef.current = serializeLines(lines);
      }
      initialFormDataRef.current = JSON.parse(JSON.stringify(data));
      onRelatedDataChanged?.();
      if (close) onCancel();
      return true;
    } catch (err: any) {
      setError(err?.message || 'Failed to save change order');
      console.error('Error submitting change order:', err);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  // "Create PO": the Purchase Order form (the same one the Purchase Orders page uses) opens OVER this form, with a COPY of the items on screen -- never the
  // change order's own rows, so editing the order cannot change them. Order of events: 1) the people it depends on (the assignee, the vendor of the items) are
  // asked to join the project, only when they are not members -- cancelling changes nothing and the form stays as it is; 2) the change order is saved (a new one
  // gets its id here) WITHOUT closing the form; 3) the purchase order opens, already linked to it (purchase_orders.change_order_id; a change order has at most one).
  // Saved or cancelled, closing the purchase order returns to this form.
  const creatingPurchaseOrder = useRef(false);
  const handleCreatePurchaseOrder = async () => {
    if (creatingPurchaseOrder.current) return; // a second click while the first one is still working does nothing
    const data = currentFormData;
    if (!data.title?.trim() || !data.projectId) { setError('Enter a title and a project before creating the purchase order.'); return; }
    if (lines.length === 0) { setError('Add at least one item before creating the purchase order.'); return; }
    creatingPurchaseOrder.current = true;
    try {
      const vendorId = commonVendorOf(lines);
      const nameOf = (id: string, fallback: string) => { const person = people.find((u) => u.id === id); return person?.company_name || person?.full_name || fallback; };
      const allowed = await ensureMembers(data.projectId, [
        data.assigneeId ? { id: data.assigneeId as string, name: nameOf(data.assigneeId as string, 'The assignee') } : null,
        vendorId ? { id: vendorId, name: nameOf(vendorId, 'The vendor') } : null,
      ], 'Add'); // this button only adds the people: the change order is saved right after
      if (!allowed) { setError('The assignee and the vendor must belong to the project before the purchase order can be created. Nothing was saved.'); return; }
      const needsSave = !changeOrderId || hasFormChanged(data, initialFormDataRef.current) || linesChanged() || selectedFiles.length > 0;
      if (needsSave && !(await handleSubmit(data, { close: false }))) return;
      setPurchaseOrderStart({
        title: data.title.trim(),
        projectId: data.projectId,
        description: derivedDescription({ type: 'Change Order', number: initialData?.coNumber, title: data.title, description: data.description }), // its name, then its own Description
        vendorId,
        lines: lines.map((line) => ({ ...line, key: newLineKey() })),
      });
    } finally {
      creatingPurchaseOrder.current = false;
    }
  };

  // Browse Drive follows the form's own Project field
  const attachmentsSection = (projectId: string) => (
    <GenericAttachmentsSection
      projectId={projectId}
      attachments={uploadedFiles}
      pendingFiles={selectedFiles}
      onUpload={handleFileUpload}
      onDelete={handleDeleteFile}
      onRemovePending={handleRemovePendingFile}
      mode={mode}
    />
  );

  const commentsSection = (
    <GenericCommentsSection
      comments={comments}
      onAddComment={handleAddComment}
      onDeleteComment={handleDeleteComment}
      mode={mode}
      audiences={TEAM_CLIENT_VENDOR_AUDIENCES}
      placeholder={mode === 'create' ? 'Comments are available after creating the change order' : 'Add a comment...'}
    />
  );

  const customFooterButtons = (
    <div className="flex gap-2">
      <CancelButton onClick={handleCloseAttempt}>
        Cancel
      </CancelButton>
      <StandardButton
        type="button"
        customVariant="white"
        onClick={handleSendToClient}
        disabled={isSendingEmail || mode === 'create' || !changeOrderId}
      >
        {isSendingEmail ? 'Sending...' : 'Send to Client'}
      </StandardButton>
      <StandardButton
        type="button"
        customVariant="white"
        onClick={handleExportPDF}
        disabled={mode === 'create' || !changeOrderId}
      >
        Export PDF
      </StandardButton>
      <StandardButton
        type="button"
        customVariant="white"
        onClick={handleCreatePurchaseOrder}
        disabled={isSubmitting || !linesLoaded || linkedDocs.length > 0}
        title={linkedDocs.length > 0 ? 'This change order already has a purchase order' : undefined}
      >
        Create PO
      </StandardButton>
      <SaveChangesButton disabled={isSubmitting}>
        {isSubmitting ? 'Saving...' : 'Save Changes'}
      </SaveChangesButton>
    </div>
  );

  return (
    <>
      {membershipPrompt}
      <GenericForm
        title="Change Order"
        mode={mode}
        fields={changeOrderFields}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        isOpen={isOpen}
        additionalSections={(values) => (
          <>
            {/* Items: the same table as a purchase order. Builder Price / Client Price above are their total. */}
            <CatalogLineItems
              catalog={catalog}
              lines={lines}
              vendorId={null}
              vendors={vendors}
              loading={linesLoading}
              loadError={linesLoadError}
              disabled={!linesLoaded}
            />
            <LinkedDocsSection docs={linkedDocs} emptyText="No linked documents" hasUnsavedChanges={() => hasFormChanged(currentFormData, initialFormDataRef.current)} />
            {attachmentsSection(values.projectId || '')}
            {commentsSection}
          </>
        )}
        error={error}
        submitButtonText="Save Changes"
        saving={isSubmitting}
        customFooterButtons={customFooterButtons}
        onFieldChange={handleFieldChange}
        hasUnsavedChanges={() => hasFormChanged(currentFormData, initialFormDataRef.current)}
      />
      {/* A sibling of the form (not inside it): a submit inside the purchase order form must never submit the change order */}
      <PurchaseOrderCreateDialog
        isOpen={purchaseOrderStart !== null}
        onClose={() => setPurchaseOrderStart(null)}
        onCreated={() => { setPurchaseOrderStart(null); void loadLinkedDocs(); onRelatedDataChanged?.(); }}
        defaults={purchaseOrderStart ?? undefined}
        changeOrderId={changeOrderId}
      />
    </>
  );
}
